import { useState, useEffect } from "react";
import { useToast } from '../../../shared/toastMessages/toastProvider';

import {
    Typography,
    Box,
    Stack,
    Paper,
    Grid,
    Skeleton,
    Chip,
} from '@mui/material';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import GppBadOutlinedIcon from '@mui/icons-material/GppBadOutlined';
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined';
import OutboxOutlinedIcon from '@mui/icons-material/OutboxOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import ReportGmailerrorredOutlinedIcon from '@mui/icons-material/ReportGmailerrorredOutlined';
import AdsClickOutlinedIcon from '@mui/icons-material/AdsClickOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

import getApiData from '../../../../hooks/apiRequests/getRequest';
import Graph from './graphs/graph';
import ContentContainer from "../../../shared/dashboard/ContentContainer";
import { theme } from '../../../../configs/theme';

const cardSx = {
    bgcolor: '#fff',
    borderRadius: '15px',
    boxShadow: '1px 5px 5px 1px rgba(0,0,0,0.12)',
    p: { xs: 2, md: 2.5 },
    height: '100%',
};

const SummaryCard = ({ icon, label, value, progress, hint, tone }) => {
    const toneStyles = {
        success: { bgcolor: '#e8f5e9', color: '#2e7d32' },
        error: { bgcolor: '#ffebee', color: '#c62828' },
        warning: { bgcolor: '#fff8e1', color: '#f57f17' },
        neutral: { bgcolor: '#f5f5f5', color: '#424242' },
        primary: { bgcolor: '#ffebee', color: theme.palette.primary.main },
    };
    const colors = toneStyles[tone] || toneStyles.neutral;

    return (
        <Paper elevation={0} sx={cardSx}>
            <Stack spacing={1.5}>
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '10px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        ...colors,
                    }}
                >
                    {icon}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    {label}
                </Typography>
                {progress ? (
                    <Skeleton variant="text" width="60%" height={36} />
                ) : (
                    <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}>
                        {value ?? '—'}
                    </Typography>
                )}
                {hint && !progress ? (
                    <Typography variant="caption" color="text.secondary">
                        {hint}
                    </Typography>
                ) : null}
            </Stack>
        </Paper>
    );
};

const SectionCard = ({ title, children }) => (
    <Paper elevation={0} sx={cardSx}>
        <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>
            {title}
        </Typography>
        {children}
    </Paper>
);

const deliverabilityTone = (value) => {
    const v = (value || '').toUpperCase();
    if (v === 'DELIVERABLE') return 'success';
    if (v === 'RISKY') return 'warning';
    if (v === 'UNDELIVERABLE') return 'error';
    return 'neutral';
};

// APIVoid score /100: ≤30 red, ≤60 amber band, ≥70 green
const healthScoreTone = (score) => {
    if (score == null || Number.isNaN(Number(score))) return 'neutral';
    const n = Number(score);
    if (n <= 30) return 'error';
    if (n < 70) return 'warning';
    return 'success';
};

const formatRate = (value) => {
    if (value == null || Number.isNaN(Number(value))) return '—';
    return `${Number(value).toFixed(2)}%`;
};

const formatWeekRange = (stats) => {
    if (!stats?.weekStart || !stats?.weekEnd) return 'This week';
    const start = new Date(stats.weekStart).toLocaleDateString('en-GB', {
        month: 'short',
        day: 'numeric',
    });
    const end = new Date(stats.weekEnd).toLocaleDateString('en-GB', {
        month: 'short',
        day: 'numeric',
    });
    return `${start} – ${end}`;
};

const Dashboard = () => {
    const [data, setData] = useState();
    const [progress, setProgress] = useState(false);
    const { showToast } = useToast();

    useEffect(() => {
        const fetchData = async () => {
            setProgress(true);
            try {
                const response = await getApiData(`/Dashboard`);
                if (!response?.data) {
                    throw new Error('Empty dashboard response');
                }
                setData(response.data);
            } catch (error) {
                console.error("Error fetching data:", error);
                showToast("Failed to load dashboard data.", "error");
            } finally {
                setProgress(false);
            }
        };

        fetchData();
    }, [showToast]);

    const health = data?.health;
    const banned = data?.banned;
    const stats = data?.post_mark_stats;
    const statsAvailable = Boolean(stats?.available);

    const banLabel = banned
        ? (banned.banned ? 'Restricted' : 'Clear')
        : null;

    return (
        <Box sx={{ px: { xs: 1, md: 2 }, pb: 3 }}>
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent="space-between"
                spacing={1}
                sx={{ py: 2 }}
            >
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: 'black' }}>
                        Dashboard
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Sender health, Postmark status, and weekly send performance.
                    </Typography>
                </Box>
                {health?.email && !progress ? (
                    <Chip
                        label={health.email}
                        size="small"
                        sx={{ bgcolor: '#f5f5f5', fontWeight: 500 }}
                    />
                ) : null}
            </Stack>

            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <SummaryCard
                        icon={<SpeedOutlinedIcon fontSize="small" />}
                        label="Health score"
                        value={health?.score != null ? health.score : null}
                        progress={progress}
                        hint="APIVoid trust score"
                        tone={healthScoreTone(health?.score)}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <SummaryCard
                        icon={<MarkEmailReadOutlinedIcon fontSize="small" />}
                        label="Deliverability"
                        value={health?.deliverability || null}
                        progress={progress}
                        tone={deliverabilityTone(health?.deliverability)}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <SummaryCard
                        icon={<GppBadOutlinedIcon fontSize="small" />}
                        label="Sender status"
                        value={banLabel}
                        progress={progress}
                        hint={banned?.banned ? banned.reason : 'Ready to send'}
                        tone={banned?.banned ? 'error' : 'success'}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <SummaryCard
                        icon={<OutboxOutlinedIcon fontSize="small" />}
                        label="Sent / Bounced"
                        value={
                            statsAvailable
                                ? `${stats.sent ?? 0} / ${stats.bounced ?? 0}`
                                : progress
                                  ? null
                                  : 'Unavailable'
                        }
                        progress={progress}
                        hint={formatWeekRange(stats)}
                        tone={statsAvailable ? 'neutral' : 'warning'}
                    />
                </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <SectionCard title="Email Health">
                        <ContentContainer
                            title="Email"
                            content={health?.email}
                            progress={progress}
                        />
                        <ContentContainer
                            title="Deliverability"
                            content={health?.deliverability}
                            progress={progress}
                            variant={deliverabilityTone(health?.deliverability)}
                        />
                        <ContentContainer
                            title="Disposable"
                            content={health?.is_disposable}
                            progress={progress}
                            variant={health?.is_disposable ? 'error' : 'success'}
                        />
                        <ContentContainer
                            title="Role account"
                            content={health?.is_role_account}
                            progress={progress}
                            variant={health?.is_role_account ? 'warning' : 'success'}
                        />
                        <ContentContainer
                            title="Catch all"
                            content={health?.is_catch_all}
                            progress={progress}
                            variant={health?.is_catch_all ? 'warning' : 'success'}
                        />
                        <ContentContainer
                            title="Score"
                            content={health?.score}
                            progress={progress}
                        />
                    </SectionCard>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                    <SectionCard title="Sender Status">
                        <ContentContainer
                            title="Email"
                            content={banned?.email || health?.email}
                            progress={progress}
                        />
                        <ContentContainer
                            title="Status"
                            content={banned?.banned}
                            progress={progress}
                            variant={banned?.banned ? 'error' : 'success'}
                            chipLabel={banned?.banned ? 'Restricted' : 'Clear'}
                        />
                        <ContentContainer
                            title="Reason"
                            content={banned?.banned ? (banned?.reason || '—') : 'None'}
                            progress={progress}
                        />
                    </SectionCard>
                </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <SummaryCard
                        icon={<OutboxOutlinedIcon fontSize="small" />}
                        label="Emails sent"
                        value={statsAvailable ? stats.sent ?? 0 : null}
                        progress={progress}
                        hint="Postmark this week"
                        tone="primary"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <SummaryCard
                        icon={<ReportGmailerrorredOutlinedIcon fontSize="small" />}
                        label="Bounce rate"
                        value={statsAvailable ? formatRate(stats.bounce_rate) : null}
                        progress={progress}
                        hint={statsAvailable ? `${stats.bounced ?? 0} bounced` : 'Postmark unavailable'}
                        tone={
                            statsAvailable && Number(stats.bounce_rate) >= 5
                                ? 'error'
                                : statsAvailable && Number(stats.bounce_rate) >= 2
                                  ? 'warning'
                                  : 'neutral'
                        }
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <SummaryCard
                        icon={<VisibilityOutlinedIcon fontSize="small" />}
                        label="Opens"
                        value={
                            statsAvailable
                                ? `${stats.opens ?? 0} / ${stats.unique_opens ?? 0}`
                                : null
                        }
                        progress={progress}
                        hint="Total / unique"
                        tone="neutral"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <SummaryCard
                        icon={<AdsClickOutlinedIcon fontSize="small" />}
                        label="Clicks"
                        value={
                            statsAvailable
                                ? `${stats.total_clicks ?? 0} / ${stats.unique_links_clicked ?? 0}`
                                : null
                        }
                        progress={progress}
                        hint="Total / unique"
                        tone="neutral"
                    />
                </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <SectionCard title="Postmark Metrics">
                        <ContentContainer
                            title="Week"
                            content={formatWeekRange(stats)}
                            progress={progress}
                        />
                        <ContentContainer
                            title="Sent"
                            content={statsAvailable ? stats?.sent : null}
                            progress={progress}
                        />
                        <ContentContainer
                            title="Bounced"
                            content={statsAvailable ? stats?.bounced : null}
                            progress={progress}
                            variant={
                                statsAvailable && (stats?.bounced ?? 0) > 0 ? 'warning' : undefined
                            }
                        />
                        <ContentContainer
                            title="Bounce rate"
                            content={statsAvailable ? formatRate(stats?.bounce_rate) : null}
                            progress={progress}
                        />
                        <ContentContainer
                            title="Spam complaints"
                            content={statsAvailable ? stats?.spam_complaints : null}
                            progress={progress}
                            variant={
                                statsAvailable && (stats?.spam_complaints ?? 0) > 0
                                    ? 'error'
                                    : 'success'
                            }
                        />
                        <ContentContainer
                            title="Spam rate"
                            content={statsAvailable ? formatRate(stats?.spam_complaints_rate) : null}
                            progress={progress}
                        />
                    </SectionCard>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <SectionCard title="Engagement">
                        <ContentContainer
                            title="Opens"
                            content={statsAvailable ? stats?.opens : null}
                            progress={progress}
                        />
                        <ContentContainer
                            title="Unique opens"
                            content={statsAvailable ? stats?.unique_opens : null}
                            progress={progress}
                        />
                        <ContentContainer
                            title="Total clicks"
                            content={statsAvailable ? stats?.total_clicks : null}
                            progress={progress}
                        />
                        <ContentContainer
                            title="Unique links clicked"
                            content={statsAvailable ? stats?.unique_links_clicked : null}
                            progress={progress}
                        />
                        <ContentContainer
                            title="Tracked emails"
                            content={statsAvailable ? stats?.tracked : null}
                            progress={progress}
                        />
                        <ContentContainer
                            title="SMTP API errors"
                            content={statsAvailable ? stats?.smtp_api_errors : null}
                            progress={progress}
                            variant={
                                statsAvailable && (stats?.smtp_api_errors ?? 0) > 0
                                    ? 'warning'
                                    : undefined
                            }
                        />
                    </SectionCard>
                </Grid>
            </Grid>

            <Paper elevation={0} sx={cardSx}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <MailOutlineIcon fontSize="small" color="action" />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Weekly Postmark activity
                    </Typography>
                </Stack>
                {progress ? (
                    <Skeleton variant="rounded" width="100%" height={360} />
                ) : (
                    <Graph Data={statsAvailable ? stats : null} theme={theme} />
                )}
            </Paper>
        </Box>
    );
};

export default Dashboard;
